import React from 'react';
import { View, Text, ScrollView, TouchableOpacity, Platform, Dimensions } from 'react-native';
import type { CalendarEvent, CalendarTheme } from '../types';
import { styles } from '../styles';

interface DayEventsProps {
  dayEvents: CalendarEvent[];
  renderEventBadge: (event: CalendarEvent, small?: boolean) => React.ReactNode;
  onEventDetailsPress?: (event: CalendarEvent) => void;
  onEventPress?: (event: CalendarEvent) => void;
  onShowAllEvents?: () => void;
  variant?: 'monthly' | 'weekly-mobile' | 'weekly-web';
  theme: CalendarTheme;
}

/**
 * DayEvents Component
 * Renders event badges with scrolling and +N indicator for overflow
 */
export const DayEvents: React.FC<DayEventsProps> = ({
  dayEvents,
  renderEventBadge,
  onEventDetailsPress,
  onEventPress,
  onShowAllEvents,
  variant = 'monthly',
  theme,
}) => {
  const isWeeklyWeb = variant === 'weekly-web';
  const isWeeklyMobile = variant === 'weekly-mobile';
  const isSmall = isWeeklyWeb || isWeeklyMobile;

  const { width: SCREEN_WIDTH } = Dimensions.get('window');
  const isIOSMobile = Platform.OS === 'ios' && SCREEN_WIDTH < 768;

  if (dayEvents.length === 0) {
    return null;
  }

  // Adjust max height based on variant to prevent overlap
  const maxHeightStyle = isWeeklyWeb || isWeeklyMobile ? { maxHeight: 75 } : { maxHeight: 100 };

  // If on iOS mobile and there's exactly one event, render it at the bottom of the cell
  if (dayEvents.length === 1 && isIOSMobile) {
    const event = dayEvents[0];
    return (
      <View style={[{ flex: 1, justifyContent: 'flex-end', paddingBottom: 6, width: '100%' }]}
      >
        <TouchableOpacity
          key={event.id}
          onPress={() => {
            onEventPress?.(event);
            onEventDetailsPress?.(event);
          }}
          activeOpacity={0.8}
        >
          {renderEventBadge(event, isSmall)}
        </TouchableOpacity>
      </View>
    );
  }
  return (
    <View style={[maxHeightStyle, { flex: 1, width: '100%' }]}>
      <ScrollView
        style={[
          isWeeklyWeb ? styles.weeklyEventsContainer : (isWeeklyMobile ? styles.weeklyEventsContainer : styles.eventsContainer),
          { flex: 1 },
        ]}
        contentContainerStyle={{ flexGrow: 1 }}
        scrollEnabled={dayEvents.length > (isSmall ? 3 : 3)}
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled={true}
      >
        {dayEvents.slice(0, 2).map((event) => (
          <TouchableOpacity
            key={event.id}
            onPress={() => {
              onEventPress?.(event);
              onEventDetailsPress?.(event);
            }}
          >
            {renderEventBadge(event, isSmall)}
          </TouchableOpacity>
        ))}
        {dayEvents.length > 2 && (
          <TouchableOpacity
            onPress={onShowAllEvents}
            style={{
              backgroundColor: theme.overflowBadgeBackgroundColor,
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 12,
              marginTop: 4,
              alignSelf: 'flex-start',
            }}
          >
            <Text style={{ fontSize: 12, fontWeight: '600', color: theme.overflowBadgeTextColor }}>
              +{dayEvents.length - 2}
            </Text>
          </TouchableOpacity>
        )}
      </ScrollView>
    </View>
  );
};

export default DayEvents;
